import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate, useParams } from '@/providers/RouterShim';
import useBranch from '../../../data/branch/useBranch';
import useBranches from '../../../data/branch/useBranches';
import useQuickSearch from '../../../data/quickSearch/useQuickSearch';
import type { VehicleListItem } from '../../../data/vehicles/useVehicles';
import useKeyboardShortcut from '../../../hooks/useKeyboardShortcut';
import Alert from '../../../styled-components/components/Alert';
import QuickMenu from '../../../styled-components/components/QuickMenu';
import { size } from '../../../styled-components/layout/helpers';
import useClarityTracking from '../../../tracking/useClarityTracking';
import useTracking from '../../../tracking/useTracking';
import { useNavigationTree } from '../NavigationV2/NavigationProvider';
//import useNavigationTree from '../../../hooks/navigation/useNavigationTree';
import Sections from './components/Sections';
import { findPagesWithTerm, MIN_SEARCH_TERM_LENGTH, prepareSections } from './utils';

export interface QuickSearchResultListItem {
  header?: string;
  title: string;
  url?: string;
  isExternal?: boolean;
  isActive?: boolean;
  vehicle?: VehicleListItem;
  doNotHighlight?: boolean;
  tone?: 'secondary';
  type: 'vehicle' | 'vehicle-listing' | 'page' | 'faq' | 'ai-toggle';
  icon?: React.ReactNode;
}

export interface QuickSearchResultListSection {
  title: string;
  subtitle?: string;
  items: QuickSearchResultListItem[];
  query?: string;
}

const Alerts = styled.div`
  margin-top: ${size(0.5)};
`;

function useKeyboardNavigation({
  navigateResults,
  navigateToActive,
  closeQuickMenu,
}: {
  navigateResults: (direction: 'up' | 'down') => void;
  navigateToActive: () => void;
  closeQuickMenu: () => void;
}) {
  useKeyboardShortcut(() => navigateResults('up'), { code: 'ArrowUp', preventDefault: true });
  useKeyboardShortcut(() => navigateResults('down'), { code: 'ArrowDown', preventDefault: true });
  useKeyboardShortcut(navigateToActive, { code: 'Enter' });
  useKeyboardShortcut(closeQuickMenu, { code: 'Escape' });
}

export interface QuickMenuOptions {
  useAi: boolean;
}

interface GlobalQuickMenuProps {
  options: QuickMenuOptions;
  closeQuickMenu: () => void;
  setQuickMenu: (options: QuickMenuOptions) => void;
}

const GlobalQuickMenu = ({ options, closeQuickMenu, setQuickMenu }: GlobalQuickMenuProps) => {
  const { t } = useTranslation(['quickSearch']);
  const [query, setQuery] = React.useState('');

  const [search, requestSearch] = useQuickSearch(query, !!options.useAi);
  const navigationTree = useNavigationTree();

  const { branchId } = useParams();
  const [branches] = useBranches();
  const [branch] = useBranch();
  const [{ itemIndex, sectionIndex, fromHover }, setActive] = useState<{
    sectionIndex: number;
    itemIndex: number;
    fromHover?: boolean;
  }>({
    sectionIndex: 0,
    itemIndex: 0,
  });
  const navigate = useNavigate();
  const track = useTracking();
  const trackClarity = useClarityTracking();

  const branchInfo = {
    branchId,
    branchName: branch?.name,
  };

  const toggleAi = () => setQuickMenu({ useAi: !options.useAi });

  // Map of refs for list items
  const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map());

  const getItemRef =
    (sectionIndex: number, itemIndex: number) => (node: HTMLLIElement | undefined) => {
      const key = `${sectionIndex}-${itemIndex}`;
      if (node) {
        itemRefs.current.set(key, node);
      } else {
        itemRefs.current.delete(key);
      }
    };

  const navigationHits = findPagesWithTerm(navigationTree, query);

  const [hasHits, sections] = prepareSections(search, navigationHits, query, branchId, t, options);

  useEffect(() => {
    if (typeof search === 'undefined' || query.length < MIN_SEARCH_TERM_LENGTH) {
      return;
    }

    const hitCount = search.total_hit_count + navigationHits.length;

    const information: any = {
      searchTerm: query,
      usingAi: options.useAi,
      branchInfo,
      hitCount,
    };

    track({ eventType: 'global-search-searched', information });
    trackClarity('global-search-searched');
  }, [requestSearch.isSuccess]);

  useEffect(() => {
    if (fromHover) {
      return;
    }

    // Scroll the active item into view
    const key = `${sectionIndex}-${itemIndex}`;
    const activeItemNode = itemRefs.current.get(key);
    if (activeItemNode) {
      activeItemNode.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [sectionIndex, itemIndex, fromHover]);

  useEffect(() => {
    const information: any = {
      branchInfo,
    };

    track({ eventType: 'global-search-opened', information });
    trackClarity('global-search-opened');
  }, []);

  const sectionsWithActive = sections.map((section, sIndex) => ({
    ...section,
    items: section.items.map((item, iIndex) => ({
      ...item,
      isActive: sIndex === sectionIndex && iIndex === itemIndex,
    })),
  }));

  const navigateResults = (direction: 'up' | 'down') => {
    const totalSections = sections.length;
    const maxItemsInCurrentSection = sections[sectionIndex]?.items.length;

    if (direction === 'down') {
      if (itemIndex < maxItemsInCurrentSection - 1) {
        setActive((active) => ({ ...active, itemIndex: active.itemIndex + 1, fromHover: false }));
      } else if (sectionIndex < totalSections - 1) {
        setActive((active) => ({
          sectionIndex: active.sectionIndex + 1,
          itemIndex: 0,
          fromHover: false,
        }));
      }
    } else if (direction === 'up') {
      if (itemIndex > 0) {
        setActive((active) => ({ ...active, itemIndex: active.itemIndex - 1, fromHover: false }));
      } else if (sectionIndex > 0) {
        setActive((active) => ({
          sectionIndex: active.sectionIndex - 1,
          itemIndex: sections[active.sectionIndex - 1].items.length - 1,
          fromHover: false,
        }));
      }
    }
  };

  const getItemTypeContext = (item: QuickSearchResultListItem) => {
    switch (item.type) {
      case 'vehicle':
        return item.vehicle?.vehicle.registrationNumber;
      case 'vehicle-listing':
      case 'page':
      case 'faq':
        return item.url;
      case 'ai-toggle':
        return undefined;
    }
  };

  const navigateToItem = (item: QuickSearchResultListItem) => {
    const information: any = {
      type: item.type,
      typeContext: getItemTypeContext(item),
      searchTerm: query,
      usingAi: options.useAi,
      branchInfo,
    };

    track({ eventType: 'global-search-navigated', information });
    trackClarity('global-search-navigated');

    if (item.isExternal) {
      window.open(item.url, '_blank');
    } else if (item.type === 'ai-toggle') {
      toggleAi();
    } else if (item.url) {
      navigate(item.url);
      closeQuickMenu();
    }
  };

  const navigateToActiveItem = () => {
    const activeItem = sections[sectionIndex]?.items[itemIndex];

    if (activeItem) {
      navigateToItem(activeItem);
    }
  };

  useKeyboardNavigation({
    navigateResults,
    navigateToActive: navigateToActiveItem,
    closeQuickMenu,
  });

  if (!options) {
    return null;
  }

  return (
    <QuickMenu
      useAi={!!options.useAi}
      loading={requestSearch.isLoading}
      onClose={closeQuickMenu}
      onSearch={(value) => {
        setQuery(value);
        setActive({ sectionIndex: 0, itemIndex: 0 });
      }}
      onToggleAi={toggleAi}
    >
      {sectionsWithActive && !requestSearch.isLoading && (
        <Sections
          query={query}
          sections={sectionsWithActive}
          useAi={!!options.useAi}
          getItemRef={getItemRef}
          handleItemClick={navigateToItem}
          handleItemHover={(sectionIndex: number, itemIndex: number) => {
            setActive({ sectionIndex, itemIndex, fromHover: true });
          }}
          branches={branches || []}
        />
      )}

      {requestSearch.isError && (
        <Alerts>
          <Alert tone="error" message={t('searchError', { ns: 'quickSearch' })} />
        </Alerts>
      )}
      {requestSearch.isSuccess && !hasHits && (
        <Alerts>
          <Alert tone="info" message={t('noResults', { ns: 'quickSearch' })} />
        </Alerts>
      )}
    </QuickMenu>
  );
};

export default GlobalQuickMenu;
